import { Link } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/base/SectionHeading";
import { communityStats, contentPrinciples, siteInfo, testimonials } from "@/mocks/site";

const milestones = [
  { year: "2024 秋", title: "从一条小记录开始", desc: "最初只是把身边的一件小事写下来贴在网上，没想到收到十几条类似经历的回复。" },
  { year: "2025 春", title: "六大分类成型", desc: "投稿越来越多，于是整理出城市身边事、消费观察、职场见闻等六个主题，方便检索。" },
  { year: "2025 冬", title: "评论区互动上线", desc: "大家希望不只读，还能补充与讨论。评论区从那时起成为网站最活跃的地方。" },
  { year: "2026 秋", title: "私信通道全面开放", desc: "为了接收图片、录音等更复杂的线索，我们增设了匿名私信通道，与投稿表单并行。" },
];

export default function About() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于爆料专区",
      description: siteInfo.description,
      url: `${origin}/about`,
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteInfo.name,
      description: siteInfo.description,
      email: siteInfo.email,
      url: origin,
    },
  ];

  return (
    <div className="bg-background-100">
      <SeoHead
        title="关于我们 - 匿名爆料社区的品牌与内容原则 | 爆料专区"
        description="了解爆料专区的品牌介绍、网站定位、发展理念与内容原则。我们坚持匿名投稿、保护当事人、让评论区讨论更有价值，也欢迎你通过私信通道联系我们。"
        keywords="关于爆料专区,匿名爆料社区,内容原则,网站定位,爆料专区"
        path="/about"
      />
      <JsonLd data={schema} />

      <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <header className="mt-5 max-w-3xl">
          <h1 className="font-display text-3xl text-foreground-950 md:text-4xl">关于爆料专区</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            我们想做的事很朴素：给每一个普通人一个可以放心开口的地方。匿名投稿、私信通道、评论区留言，
            三条路径同时开放，让身边那些容易被忽略的小事被更多人看见。
          </p>
        </header>

        <section className="mt-10 grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12" aria-label="品牌介绍">
          <div className="h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-[360px]">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20editorial%20photograph%20of%20a%20community%20notice%20board%20covered%20with%20handwritten%20notes%20and%20small%20photos%20in%20soft%20afternoon%20light%2C%20candid%20documentary%20style%2C%20muted%20amber%20terracotta%20palette%2C%20shallow%20depth%20of%20field%2C%20high%20detail&width=1200&height=900&seq=baoliao-about-01&orientation=landscape"
              alt="贴满手写纸条与小照片的社区留言板"
              title="爆料专区 - 一个由普通人共同记录的地方"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <SectionHeading
              kicker="网站定位"
              title="不是媒体，是一面大家一起照的镜子"
              desc="爆料专区不做评判，也不替任何人下定论。我们只做一件事：把投稿人亲身经历的内容整理清楚，隐去不必要的个人信息，然后让它被看见。"
            />
            <ul className="mt-6 flex flex-col gap-4">
              {contentPrinciples.map((item) => (
                <li key={item.title} className="flex gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-lg`} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-label text-sm font-medium text-foreground-900">{item.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-14 rounded-lg bg-secondary-100 px-5 py-10 md:px-10 md:py-12" aria-label="发展理念">
          <SectionHeading
            kicker="发展理念"
            title="慢一点，但把每一条做扎实"
            desc="我们不追求数量，也不鼓励未经核实的指控。与其让十条模糊的传闻刷屏，不如把一条讲清楚。"
          />
          <ol className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {milestones.map((item) => (
              <li key={item.year} className="flex flex-col rounded-lg border border-secondary-200 bg-background-50 p-5">
                <span className="inline-flex w-fit items-center rounded-full bg-accent-500 px-3 py-1 font-label text-xs font-medium text-foreground-950">
                  {item.year}
                </span>
                <h3 className="mt-4 font-display text-base text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-14" aria-label="社区现状">
          <dl className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {communityStats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-6 text-center">
                <dd className="flex items-baseline justify-center gap-1">
                  <span className="font-display text-2xl text-foreground-950 md:text-3xl">{stat.value}</span>
                  <span className="font-label text-xs text-foreground-500">{stat.unit}</span>
                </dd>
                <dt className="mt-1 font-label text-xs text-foreground-600">{stat.label}</dt>
              </div>
            ))}
          </dl>
        </section>

        <section className="mt-14" aria-label="用户口碑">
          <SectionHeading
            kicker="用户口碑"
            title="他们这样用爆料专区"
            desc="来自投稿人和常驻读者的真实反馈，我们只做了简单整理。"
          />
          <ul className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <li key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true" />
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                <div className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-200 text-secondary-800">
                    <i className={`${item.avatar} text-lg`} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-label text-sm font-medium text-foreground-900">{item.name}</p>
                    <p className="font-label text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10" aria-label="联系方式">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <h2 className="font-display text-2xl text-foreground-950">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                有线索要补充、想请求删除某条内容，或对网站有任何建议，都可以通过下面的方式找到我们。
                除了以下渠道，我们没有其他联系方式。
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={`mailto:${siteInfo.email}`}
                  rel="nofollow"
                  className="inline-flex items-center gap-3 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-3 font-label text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-mail-line text-lg text-primary-600" aria-hidden="true" />
                  {siteInfo.email}
                </a>
                <Link
                  to="/submit"
                  className="inline-flex items-center gap-3 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-3 font-label text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-chat-private-line text-lg text-primary-600" aria-hidden="true" />
                  私信通道 · 匿名补充线索
                </Link>
              </div>
            </div>
            <div className="flex flex-col justify-center rounded-lg bg-primary-950 p-6 text-background-100">
              <i className="ri-megaphone-fill text-2xl text-accent-400" aria-hidden="true" />
              <h3 className="mt-3 font-display text-xl text-background-50">有话想说，就现在写下来</h3>
              <p className="mt-2 text-sm leading-relaxed text-background-200">
                不需要准备什么，写下你看到的那件事就好。剩下的交给我们。
              </p>
              <Link
                to="/submit"
                className="mt-5 inline-flex w-fit items-center justify-center whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400"
              >
                我要爆料
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}